<template>
  <div style="background:#fff; padding:20px;">
    <!-- 顶部高级搜索区: 对应 design.md「后台日志高级查询」口径 -->
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <!-- 操作人 ID, 精确匹配 sys_logs.operator_id (后端预留扩展字段) -->
        <a-form-item label="操作人ID">
          <a-input-number
            v-model="query.operatorId"
            :min="1"
            placeholder="请输入操作人ID"
            style="width: 140px"
          />
        </a-form-item>

        <!-- 操作人账号, 精确匹配 sys_logs.username -->
        <a-form-item label="操作人账号">
          <a-input
            v-model="query.operatorAccount"
            placeholder="请输入操作人账号"
            allow-clear
            style="width: 160px"
          />
        </a-form-item>

        <!-- 操作类型多选, 后端按 operation 字段以 [ENUM_NAME] 前缀匹配 -->
        <a-form-item label="操作类型">
          <a-select
            v-model="query.operationTypes"
            mode="multiple"
            placeholder="可多选"
            allow-clear
            style="min-width: 260px"
          >
            <a-select-option
              v-for="opt in operationTypeOptions"
              :key="opt.value"
              :value="opt.value"
            >{{ opt.label }}</a-select-option>
          </a-select>
        </a-form-item>

        <!-- 时间区间, 客户端强制跨度 ≤ 90 天, 与后端校验保持一致 -->
        <a-form-item label="时间区间">
          <a-range-picker
            v-model="query.timeRange"
            :show-time="{ format: 'HH:mm:ss' }"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :placeholder="['起始时间', '结束时间']"
            style="width: 380px"
          />
        </a-form-item>

        <a-form-item>
          <!-- 沿用既有权限码 system:log:list, 与后端 @PreAuthorize 一致 -->
          <a-button
            type="primary"
            icon="search"
            v-permission="'system:log:list'"
            @click="handleSearch"
          >查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- 列表: 既有列保持不变 -->
    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
    </a-table>
  </div>
</template>

<script>
import moment from 'moment'
import { getLogPage } from '@/api'

export default {
  data() {
    return {
      data: [],
      loading: false,
      // 操作类型枚举, 与后端 Log.OperationType 保持一致
      operationTypeOptions: [
        { value: 'AUDIT_MERCHANT', label: '入驻审核' },
        { value: 'TOGGLE_USER_STATUS', label: '小程序用户启用/禁用' },
        { value: 'GRANT_MEMBERSHIP', label: '后台增加会员天数' },
        { value: 'FORCE_CLOSE_TICKET', label: '强制关闭工单' },
        { value: 'AUDIT_WITHDRAW', label: '提现审核' },
        { value: 'MAINTAIN_DICT', label: '字典维护' },
        { value: 'AUDIT_TASK', label: '任务审核' },
        { value: 'OTHER', label: '其他' }
      ],
      // 查询条件
      query: {
        operatorId: undefined,
        operatorAccount: undefined,
        operationTypes: [],
        // a-range-picker 由 valueFormat 声明返回 [startStr, endStr]
        timeRange: []
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      // 既有 columns 保持不变, 沿用 username / operation / method / params / time / ip / createTime
      columns: [
        { title: '操作者', dataIndex: 'username', width: '100px' },
        { title: '操作名称', dataIndex: 'operation', width: '200px' },
        { title: '方法名', dataIndex: 'method', ellipsis: true },
        { title: '参数', dataIndex: 'params', ellipsis: true },
        { title: '耗时(ms)', dataIndex: 'time', width: '100px' },
        { title: 'IP', dataIndex: 'ip', width: '120px' },
        { title: '时间', dataIndex: 'createTime', width: '180px' }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 校验时间区间合法性
    // 仅在两端都填写时校验: 起 ≤ 终, 且跨度 ≤ 90 天
    // 返回 { ok, startTime, endTime, message }
    validateTimeRange() {
      const range = this.query.timeRange || []
      const start = range[0]
      const end = range[1]

      // 未填或填一半: 后端要求同时提供或同时为空, 这里也保持一致
      if (!start && !end) {
        return { ok: true, startTime: undefined, endTime: undefined }
      }
      if (!start || !end) {
        return { ok: false, message: '时间区间起止必须同时填写' }
      }

      const startMoment = moment(start)
      const endMoment = moment(end)
      if (!startMoment.isValid() || !endMoment.isValid()) {
        return { ok: false, message: '时间格式不合法' }
      }
      if (endMoment.isBefore(startMoment)) {
        return { ok: false, message: '结束时间不能早于起始时间' }
      }
      // 跨度上限 90 天, 与后端 Requirement 16.4 一致
      const diffDays = endMoment.diff(startMoment, 'days', true)
      if (diffDays > 90) {
        return { ok: false, message: '时间跨度不能超过 90 天' }
      }

      return {
        ok: true,
        // ISO 字符串便于后端 @DateTimeFormat(iso = ISO.DATE_TIME) 解析
        startTime: startMoment.toISOString(),
        endTime: endMoment.toISOString()
      }
    },
    // 加载列表
    loadData() {
      // 先做时间区间校验, 不合法直接拦截不发请求
      const checked = this.validateTimeRange()
      if (!checked.ok) {
        this.$message.error(checked.message)
        return
      }

      this.loading = true
      // 拼装请求参数
      // 注意: operationTypes 数组将被 axios 默认序列化为 ?operationTypes=A&operationTypes=B
      //       与后端 @RequestParam List<String> 解析方式兼容
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        operatorId: this.query.operatorId || undefined,
        operatorAccount: (this.query.operatorAccount || '').trim() || undefined,
        operationTypes: (this.query.operationTypes && this.query.operationTypes.length)
          ? this.query.operationTypes
          : undefined,
        startTime: checked.startTime,
        endTime: checked.endTime
      }

      getLogPage(params)
        .then(res => {
          // 兼容 MyBatis-Plus Page: { records, total } / { list, total } / 数组返回
          const payload = (res && res.data) || {}
          if (payload.records) {
            this.data = payload.records
            this.pagination.total = payload.total || 0
          } else if (payload.list) {
            this.data = payload.list
            this.pagination.total = payload.total || 0
          } else if (Array.isArray(payload)) {
            this.data = payload
            this.pagination.total = payload.length
          } else {
            this.data = []
            this.pagination.total = 0
          }
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    // 查询
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    // 重置: 清空所有过滤条件并回到第一页
    handleReset() {
      this.query = {
        operatorId: undefined,
        operatorAccount: undefined,
        operationTypes: [],
        timeRange: []
      }
      this.pagination.current = 1
      this.loadData()
    },
    // 表格分页/排序/筛选变更
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    }
  }
}
</script>
